/** @jsxImportSource @emotion/react */

import { Form as FinalForm } from 'react-final-form'
import arrayMutators from 'final-form-arrays'
import { FC, useMemo, useState } from 'react'
import { Button } from '@mui/material'
import { useMutation } from '@apollo/client'
import { useParams } from 'react-router-dom'
import { omit } from 'lodash'
import { InputField } from 'components/Input'
import { SelectField } from 'components/Select'
import { validateDefaultHtml, validateRequired, validateRequiredDefaultHtml } from 'utils/validation'
import { AdvancedFieldArray } from 'components/AdvancedFieldArray/AdvancedFieldArray'
import { Input } from 'components/Input/Input'
import { Checkbox, CheckboxField } from 'components/Checkbox'
import { UpdateUpgradePage, UpdateUpgradePageVariables } from '__generatedTypes__/UpdateUpgradePage'
import { UPDATE_UPGRADE_PAGE } from 'queries/upgradePage'
import { findAllUpgradePage_findAllUpgradePage_items } from '__generatedTypes__/findAllUpgradePage'
import { PriceInput } from 'components/Input/PriceInput'
import { convertToCents, convertToDollars } from 'utils/priceFormatter'
import { toast } from 'utils/toast'
import { PlatformSlug } from 'constants/consts'
import { getMembershipTitle } from 'utils/getMembershipTitle'
import { MainBadgeEnum, SecondaryBadgeEnum } from '__generatedTypes__/globalTypes'
import { EditorField } from 'components/Editor'
import * as classes from './styles'

const MAIN_BADGE_OPTIONS: Array<{ id: MainBadgeEnum; title: string }> = [
  { id: MainBadgeEnum.None, title: 'None' },
  { id: MainBadgeEnum.MostFlexible, title: 'Most Flexible' },
  { id: MainBadgeEnum.MostPopular, title: 'Most Popular' },
  { id: MainBadgeEnum.BestValue, title: 'Best Value' },
]

const SECONDARY_BADGE_OPTIONS: Array<{ id: SecondaryBadgeEnum; title: string }> = [
  { id: SecondaryBadgeEnum.None, title: 'None' },
  { id: SecondaryBadgeEnum.HigherScoreGuarantee, title: 'Higher Score Guarantee' },
  { id: SecondaryBadgeEnum.UpdatedForYear, title: 'Updated for Year' },
]

const PLATFORMS_WITHOUT_ORIGINAL_PRICE = [
  PlatformSlug.CARSBooster,
  PlatformSlug.CasperBooster,
  PlatformSlug.INBDEBooster,
  PlatformSlug.PATBooster,
  PlatformSlug.NBDHEBooster,
  PlatformSlug.NDEBCrusher,
  PlatformSlug.GREBooster,
  PlatformSlug.DLOSCE,
]

const PLATFORMS_WITHOUT_FEATURES = [PlatformSlug.PATBooster]

type Props = {
  data: findAllUpgradePage_findAllUpgradePage_items
}

type FormValues = {
  id: string
  membershipTitle: string
  title: string
  mainBadge: { id: MainBadgeEnum; title: string }
  secondaryBadge: { id: SecondaryBadgeEnum; title: string }
  showMaxBadge: boolean
  isPassGuaranteed: boolean
  salesPrice: number
  originalPrice: number
  features: string[]
  description: string | null
}

export const Form: FC<Props> = ({ data }) => {
  const params = useParams()
  const [isDescriptionNeeded, setIsDescriptionNeeded] = useState(false)

  const [updateUpgradePage] = useMutation<UpdateUpgradePage, UpdateUpgradePageVariables>(UPDATE_UPGRADE_PAGE)

  const handleSubmit = async (values: FormValues) => {
    const { salesPrice, originalPrice, description, mainBadge, secondaryBadge, showMaxBadge, isPassGuaranteed } = values

    try {
      await updateUpgradePage({
        variables: {
          updateUpgradePageInputFieldName: {
            ...omit(values, 'membershipTitle', 'mainBadge', 'secondaryBadge'),
            mainBadge: mainBadge.id,
            secondaryBadge: secondaryBadge.id,
            showMaxBadge,
            isPassGuaranteed,
            salesPrice: convertToCents(Number(salesPrice)),
            originalPrice: originalPrice != null ? convertToCents(Number(originalPrice)) : null,
            description: isDescriptionNeeded ? description : null,
          },
        },
      })
      toast.success(`${data.isForNewUsers ? 'Product' : 'Extension'} pricing updated successfully`)
    } catch (error) {
      toast.error(`Failed to update ${data.isForNewUsers ? 'product' : 'extension'} pricing`)
    }
  }

  const initialValues = useMemo(() => {
    const {
      id,
      title,
      isForNewUsers,
      salesPrice,
      originalPrice,
      features,
      mainDescription,
      description,
      mainBadge,
      secondaryBadge,
      showMaxBadge,
      isPassGuaranteed,
    } = data

    setIsDescriptionNeeded(!!description)

    return {
      id,
      title,
      mainBadge: MAIN_BADGE_OPTIONS.find((option) => option.id === mainBadge) ?? MAIN_BADGE_OPTIONS[0],
      secondaryBadge:
        SECONDARY_BADGE_OPTIONS.find((option) => option.id === secondaryBadge) ?? SECONDARY_BADGE_OPTIONS[0],
      showMaxBadge,
      isPassGuaranteed,
      isForNewUsers,
      salesPrice: convertToDollars(salesPrice),
      originalPrice: originalPrice != null ? convertToDollars(originalPrice) : undefined,
      features,
      mainDescription,
      description,
      membershipTitle: getMembershipTitle(data),
    }
  }, [])
  const platformSlug = params.platform as PlatformSlug

  // only CARSBooster doesn't have Secondary Badge on web because of custom design
  const withSecondaryBadge = ![PlatformSlug.CARSBooster].includes(platformSlug)
  // only these platforms have Pass Guaranteed block on web because of custom design (isNewDesign value)
  const withPassGuaranteed = [
    PlatformSlug.CasperBooster,
    PlatformSlug.INBDEBooster,
    PlatformSlug.NBDHEBooster,
    PlatformSlug.NDEBCrusher,
  ].includes(platformSlug)

  return (
    <FinalForm<FormValues>
      mutators={{ ...arrayMutators }}
      onSubmit={handleSubmit}
      initialValues={initialValues}
      render={({ handleSubmit, submitting }) => (
        <form css={classes.form} onSubmit={handleSubmit} data-testid={`${initialValues.id}-form`}>
          <InputField
            name="membershipTitle"
            validate={validateRequired}
            label={data.isForNewUsers ? 'Membership' : 'Extension'}
            disabled
            fullWidth
          />

          <InputField
            name="title"
            validate={validateRequired}
            label="Title"
            placeholder="Enter Title"
            fullWidth
            css={classes.field}
          />

          <div css={classes.field}>
            <EditorField
              type="default"
              name="mainDescription"
              label="Main Description"
              placeholder="Enter Main Description"
              validate={validateDefaultHtml}
            />
          </div>

          <SelectField
            name="mainBadge"
            label="Main Badge"
            items={MAIN_BADGE_OPTIONS}
            placeholder="Select Main Badge"
            fullWidth
            css={classes.field}
          />

          {withSecondaryBadge && (
            <SelectField
              name="secondaryBadge"
              label="Secondary Badge"
              items={SECONDARY_BADGE_OPTIONS}
              placeholder="Select Secondary Badge"
              fullWidth
              css={classes.field}
            />
          )}

          <CheckboxField name="showMaxBadge" label="Show Max Icon" css={classes.field} />

          {withPassGuaranteed && (
            <CheckboxField name="isPassGuaranteed" label="Show Pass Guaranteed" css={classes.field} />
          )}

          <PriceInput
            name="salesPrice"
            validate={validateRequired}
            label="Sale Price"
            placeholder="Enter Sale Price"
            prefix="$"
            fullWidth
            disabled
            css={classes.field}
          />

          <PriceInput
            name="originalPrice"
            validate={PLATFORMS_WITHOUT_ORIGINAL_PRICE.includes(platformSlug) ? undefined : validateRequired}
            label="Original Price"
            placeholder="Enter Original Price"
            prefix="$"
            fullWidth
            css={classes.field}
          />

          {!PLATFORMS_WITHOUT_FEATURES.includes(platformSlug) && (
            <AdvancedFieldArray<string>
              name="features"
              validate={(value) => ((value?.length ?? 0) < 1 ? 'Should be at least one feature' : undefined)}
              itemName="Content"
              label="Features"
              getEmptyItem={() => ''}
              noItemsText="No features added"
              isSortable
              validateField={validateRequired}
              css={[classes.field, classes.fieldArray]}
              renderField={({ name, value, onChange, editable, index, error }) =>
                editable ? (
                  <Input
                    name={name}
                    value={value ?? ''}
                    onChange={onChange}
                    placeholder={`Enter Feature #${index + 1}`}
                    size="small"
                    color="secondary"
                    error={!!error}
                    fullWidth
                  />
                ) : (
                  <div>{value ?? ''}</div>
                )
              }
              removeConfirmation
            />
          )}

          <Checkbox
            name="isDescriptionNeeded"
            label="Do you need description?"
            checked={isDescriptionNeeded}
            onChange={() => setIsDescriptionNeeded((isDescriptionNeeded) => !isDescriptionNeeded)}
            css={classes.field}
          />

          {isDescriptionNeeded && (
            <div css={classes.field}>
              <EditorField
                type="default"
                name="description"
                label="Description"
                placeholder="Enter Description"
                validate={validateRequiredDefaultHtml}
              />
            </div>
          )}

          <Button
            variant="outlined"
            type="submit"
            color="blue"
            size="large"
            fullWidth
            css={classes.submitButton}
            disabled={submitting}
          >
            Update
          </Button>
        </form>
      )}
    />
  )
}
